import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserRole } from './ImpersonationContext';

export type FeatureFlagKey = 
  | 'our-partners'
  | 'refer-designer'
  | 'events'
  | 'marketing'
  | 'campaigns'
  | 'guild-seal-assets'
  | 'guild-iq'
  | 'matching-profile'
  | 'my-bio'
  | 'designer-files'
  | 'designer-confessions'
  | 'client-financing'
  | 'project-reporting'
  | 'quick-links'
  | 'project-gallery'
  | 'idea-books'
  | 'guild-guides'
  | 'courses';

export interface FeatureFlag {
  key: FeatureFlagKey;
  name: string;
  description: string;
  accountTypes: ('Designer' | 'Homeowner' | 'Administrator')[];
  enabled: boolean;
}

const DEFAULT_FEATURE_FLAGS: FeatureFlag[] = [
  {
    key: 'our-partners',
    name: 'Our Partners',
    description: 'Access to partners directory and resources',
    accountTypes: ['Designer', 'Homeowner'],
    enabled: false
  },
  {
    key: 'refer-designer',
    name: 'Refer a Designer',
    description: 'Referral program for designers',
    accountTypes: ['Designer'],
    enabled: false
  },
  {
    key: 'events',
    name: 'Events',
    description: 'Access to HDG events and workshops',
    accountTypes: ['Designer', 'Homeowner'],
    enabled: false
  },
  {
    key: 'marketing',
    name: 'Marketing',
    description: 'Marketing resources and tools',
    accountTypes: ['Designer'],
    enabled: false
  },
  {
    key: 'campaigns',
    name: 'Campaigns',
    description: 'Marketing campaign management',
    accountTypes: ['Designer'],
    enabled: false
  },
  {
    key: 'guild-seal-assets',
    name: 'Guild Seal Assets',
    description: 'Downloadable brand assets and seals',
    accountTypes: ['Designer'],
    enabled: false
  },
  {
    key: 'guild-iq',
    name: 'Guild IQ',
    description: 'AI-powered design intelligence and insights',
    accountTypes: ['Designer'],
    enabled: false
  },
  {
    key: 'matching-profile',
    name: 'Matching Profile',
    description: 'Designer matching profile setup',
    accountTypes: ['Designer'],
    enabled: true // Always available for designers
  },
  {
    key: 'my-bio',
    name: 'My Bio',
    description: 'Designer biography and portfolio',
    accountTypes: ['Designer'],
    enabled: true // Always available for designers
  },
  {
    key: 'designer-files',
    name: 'Designer Files',
    description: 'Project files and portfolios',
    accountTypes: ['Designer'],
    enabled: true // Always available for designers
  },
  {
    key: 'designer-confessions',
    name: 'Designer Confessions',
    description: 'Community discussions and confessions',
    accountTypes: ['Designer'],
    enabled: false
  },
  {
    key: 'client-financing',
    name: 'Client Financing',
    description: 'Financing options for clients',
    accountTypes: ['Designer'],
    enabled: false
  },
  {
    key: 'project-reporting',
    name: 'Project Reporting',
    description: 'Project analytics and reporting',
    accountTypes: ['Designer'],
    enabled: false
  },
  {
    key: 'quick-links',
    name: 'Quick Links',
    description: 'Customizable quick access links',
    accountTypes: ['Designer', 'Homeowner'],
    enabled: false
  },
  {
    key: 'project-gallery',
    name: 'Project Gallery',
    description: 'Access to curated project gallery (Design Files)',
    accountTypes: ['Homeowner'],
    enabled: false // Default: locked/disabled
  },
  {
    key: 'idea-books',
    name: 'My Idea Books',
    description: 'Create and save to Idea Books',
    accountTypes: ['Homeowner'],
    enabled: false // Default: locked/disabled
  },
  {
    key: 'guild-guides',
    name: 'Guild Guides',
    description: 'Access to Guild Guide articles',
    accountTypes: ['Homeowner'],
    enabled: false // Default: locked/disabled
  },
  {
    key: 'courses',
    name: 'Courses',
    description: 'Access to educational courses',
    accountTypes: ['Homeowner'],
    enabled: false // Default: locked/disabled
  }
];

interface FeatureFlagsContextType {
  flags: FeatureFlag[];
  updateFlag: (key: FeatureFlagKey, updates: Partial<FeatureFlag>) => void;
  isFeatureEnabled: (key: FeatureFlagKey, accountType?: string) => boolean;
  getEnabledFeaturesForAccountType: (accountType: string) => FeatureFlag[];
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlag[]>(() => {
    // Load flags from localStorage on initialization
    const savedFlags = localStorage.getItem('featureFlags');
    if (savedFlags) {
      try {
        return JSON.parse(savedFlags);
      } catch (error) {
        return DEFAULT_FEATURE_FLAGS;
      }
    }
    return DEFAULT_FEATURE_FLAGS;
  });

  // Save flags to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('featureFlags', JSON.stringify(flags));
  }, [flags]);

  const updateFlag = (key: FeatureFlagKey, updates: Partial<FeatureFlag>) => {
    setFlags(prev => prev.map(flag => 
      flag.key === key ? { ...flag, ...updates } : flag
    ));
  };

  const isFeatureEnabled = (key: FeatureFlagKey, accountType?: string) => {
    const flag = flags.find(f => f.key === key);
    if (!flag) return false;
    
    // If accountType is provided, check if it's allowed for that account type
    if (accountType && !flag.accountTypes.includes(accountType as any)) {
      return false;
    }
    
    return flag.enabled;
  };

  const getEnabledFeaturesForAccountType = (accountType: string) => {
    return flags.filter(flag => 
      flag.enabled && flag.accountTypes.includes(accountType as any)
    );
  };

  const contextValue: FeatureFlagsContextType = {
    flags,
    updateFlag,
    isFeatureEnabled,
    getEnabledFeaturesForAccountType,
  };

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

